import { useMemo } from "react";
import { OfferPreview } from "@justhome/api-core";

/**
 * Returns the best offer with the lowest interest rate + two offers with the lowest monthly installment per fixed interest period.
 **/
const useGetBestOffers = (
  offers: OfferPreview[] | undefined,
): OfferPreview[] | null => {
  return useMemo(() => {
    if (!offers || !offers.length) {
      return null;
    }

    // Step 1: Group by fixedInterestPeriodInYears
    const groupedOffers = offers.reduce(
      (acc, offer) => {
        let group = acc[offer.fixedInterestPeriodInYears!];
        if (!group) {
          group = [];
          acc[offer.fixedInterestPeriodInYears!] = group;
        }
        group.push(offer);
        return acc;
      },
      {} as Record<string, OfferPreview[]>,
    );

    // Step 2: Get the offer with the lowest interestRate from the group with the lowest fixedInterestPeriodInYears
    const lowestFixedPeriod = Math.min(
      ...Object.keys(groupedOffers).map(Number),
    );

    const lowestFixedPeriodOffers = groupedOffers[lowestFixedPeriod];
    if (!lowestFixedPeriodOffers) return null;

    const bestShortTermOffer = lowestFixedPeriodOffers.reduce(
      (best, current) =>
        (current.interestRate ?? Infinity) < (best?.interestRate ?? Infinity)
          ? current
          : best,
      lowestFixedPeriodOffers[0],
    );

    // Step 3: Get the offer with the lowest monthlyInstallment for each fixedInterestPeriodInYears
    const bestOffersForEachFixedInterestPeriod = Object.values(groupedOffers)
      .map((group: OfferPreview[]) => {
        const filteredGroup = group.filter(
          (offer) => offer.id !== bestShortTermOffer?.id,
        );

        if (filteredGroup.length === 0) {
          return null;
        }

        return filteredGroup.reduce(
          (acc, offer) =>
            (offer.monthlyInstallment ?? Infinity) <
            (acc?.monthlyInstallment ?? Infinity)
              ? offer
              : acc,
          filteredGroup[0],
        );
      })
      .filter((offer): offer is OfferPreview => !!offer)
      .sort(
        (a, b) =>
          a!.fixedInterestPeriodInYears! - b!.fixedInterestPeriodInYears!,
      );

    // Step 4: Return the best offer with the lowest interest rate + max. 2 of the ones with the lowest monthly installment
    return [bestShortTermOffer, ...bestOffersForEachFixedInterestPeriod]
      .filter((offer): offer is OfferPreview => offer !== undefined)
      .slice(0, 3);
  }, [offers]);
};

export default useGetBestOffers;
